// Project-group header for the Roadmap > Resource view.
//
// Replaces what used to be two disconnected rows (banner + per-role labels)
// with one 80px header block that mirrors the styling of a business-function
// column header: project name where the function name sits (12px / 600 / ink,
// bottom-aligned), role labels where the function description sits (11px /
// muted / 2-line clamp).
//
// Internally the component uses `grid-template-columns: subgrid` so the
// project name spans the full role-column range while each role label
// pixel-aligns with its underlying role column in the parent grid.

import { BannerLabel } from './BannerLabel.tsx';

type Role = { id: string; display: string };

export function ResourceProjectHeader({
  display,
  roles,
  startCol,
  onRenameRole,
  onRenameProject,
  onDeleteRole,
}: {
  display: string;
  roles: Role[];
  startCol: number;
  /** Create-mode hook: when provided, each role label becomes editable. */
  onRenameRole?: (roleId: string, next: string) => void;
  /** Create-mode hook: when provided, the project name becomes editable. */
  onRenameProject?: (next: string) => void;
  /** Create-mode hook: deletes a role; only shown when there are 2+. */
  onDeleteRole?: (roleId: string) => void;
}) {
  const canDelete = onDeleteRole != null && roles.length > 1;
  return (
    <div
      className='snap-resource-project-header'
      style={{
        gridRow: 1,
        gridColumn: `${startCol} / span ${roles.length}`,
      }}
      title={display}
    >
      <div
        className={
          onRenameProject
            ? 'snap-resource-project-header__name has-hover-actions'
            : 'snap-resource-project-header__name'
        }
      >
        {onRenameProject ? (
          <BannerLabel text={display} onSave={onRenameProject} />
        ) : (
          <span>{display}</span>
        )}
      </div>
      {roles.map(r => (
        <div
          key={r.id}
          className={
            onRenameRole
              ? 'snap-resource-project-header__role has-hover-actions'
              : 'snap-resource-project-header__role'
          }
          title={r.display}
        >
          {onRenameRole ? (
            <BannerLabel
              text={r.display}
              onSave={next => onRenameRole(r.id, next)}
              onDelete={canDelete ? () => onDeleteRole!(r.id) : undefined}
            />
          ) : (
            <span>{r.display}</span>
          )}
        </div>
      ))}
    </div>
  );
}
